"use client"

import Image from "next/image"
import { ChevronDown } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { MYSTERY_BOX_PRODUCTS, type MysteryBoxItem } from "@constants/cart"

function SizeDropdown({
  sizes,
  selectedSize,
  onSelect,
  label,
}: {
  sizes: MysteryBoxItem["sizes"]
  selectedSize: string
  onSelect: (value: string) => void
  label: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const selectedLabel = sizes.find((s) => s.value === selectedSize)?.label

  return (
    <div className="relative flex-1 min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 outline-none focus-visible:ring-1 focus-visible:ring-brand-navy"
      >
        <span className={selectedLabel ? "text-gray-900" : "text-gray-500"}>
          {selectedLabel ?? "Select Size"}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-10 top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {sizes.map((size) => (
            <button
              key={size.value}
              type="button"
              role="option"
              aria-selected={size.value === selectedSize}
              onClick={() => {
                onSelect(size.value)
                setOpen(false)
              }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                size.value === selectedSize ? "text-brand-navy font-semibold" : "text-gray-700"
              }`}
            >
              {size.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function MysteryBoxCard({ item }: { item: MysteryBoxItem }) {
  const [selectedSize, setSelectedSize] = useState("")

  const handleAddToCart = () => {
    // TODO: Integrate with Medusa backend when mystery box products exist
  }

  return (
    <div className="flex items-center gap-3 bg-[#F7F7F7] rounded-lg px-3 py-2.5">
      {/* Product Image */}
      <div className="relative w-14 h-14 flex-shrink-0">
        <Image
          src={item.image}
          alt={item.name}
          fill
          className="object-contain"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        {item.popular && (
          <span className="inline-block w-fit px-2 py-0.5 text-[10px] font-semibold bg-brand-navy text-white rounded-full">
            Popular
          </span>
        )}

        {/* Name + Price */}
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-normal text-black truncate">
            {item.name}
          </span>
          <span className="text-xs font-medium text-gray-500 flex-shrink-0 whitespace-nowrap">
            (+£{item.price.toFixed(2)})
          </span>
        </div>

        {/* Size Dropdown + Add Button */}
        <div className="flex items-center gap-2">
          <SizeDropdown
            sizes={item.sizes}
            selectedSize={selectedSize}
            onSelect={setSelectedSize}
            label={`Choose size for ${item.name}`}
          />

          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!selectedSize}
            className="flex-shrink-0 px-3.5 py-1.5 bg-brand-navy text-white text-xs font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Add to cart
          </button>
        </div>
      </div>
    </div>
  )
}

export default function MysteryBox() {
  return (
    <div className="pt-4" aria-labelledby="mystery-box-heading">
      <p
        id="mystery-box-heading"
        className="text-[13px] font-bold text-brand-navy mb-2.5"
      >
        Add a mystery box
      </p>

      <div className="flex flex-col gap-2.5">
        {MYSTERY_BOX_PRODUCTS.map((item) => (
          <MysteryBoxCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  )
}
